import { FilterPanel } from "@mcleod/dispatch";
import { AutogenLayoutFuelTransactionsProcessingReady } from "./autogen/AutogenLayoutFuelTransactionsProcessingReady";
import { DataSourceAction, DataSourceExecutionEvent, TableRowDisplayEvent } from "@mcleod/components";
import { FuelTransactionTaskFilter } from "./FuelTransactionTaskFilter";
import { SecondaryFilter } from "@mcleod/powerbroker/src/BrokerageMovementTableType";
import { ProcessingHandler } from "../ProcessingHandler";
import { Action } from "../ProcessingTypes";
import { ProcessingStepper } from "../ProcessingStepper";
import { Currency } from "@mcleod/core";
import { CommonTooltips } from "@mcleod/common";

export class FuelTransactionsProcessingReady extends AutogenLayoutFuelTransactionsProcessingReady {
    private filterPanel: FilterPanel;
    private processingHandler: ProcessingHandler;

    onLoad(): void | Promise<void> {
        this.processingHandler = new ProcessingHandler({
            endpoint: "lme/integrations/fuel/post-fuel-transactions",
            title: "Batch Fuel Transaction"
        }, this.tableReadyTransactions);
        this.processingHandler.buttonPostOnClick = (action: Action) => this.processingActionOnPost(action);
        this.processingHandler.multiButton.primaryButton.disabledTooltip = "Select a provider to enable posting options";
    }

    /** This is an event handler for the beforeExecution event of sourceFuelDetail. */
    sourceFuelDetailBeforeExecution(event: DataSourceExecutionEvent) {
        event.filter.is_hold = "N";
    }

    /** This is an event handler for the afterExecution event of sourceFuelDetailList. */
    sourceFuelDetailListAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            const vendorMap = new Map();
            this.mainDataSource.data.forEach(d => {
                const id = d.get("interface_id");
                if (!vendorMap.has(id)) {
                    const interfaceLmData = d.getFirstLookupModelData("interface_id");
                    vendorMap.set(id, {
                        id: id,
                        name: interfaceLmData.get("descr")
                    });
                }
            });
            const vendors = Array.from(vendorMap.values());
            this.filterPanel = new FilterPanel(
                FuelTransactionTaskFilter.buildVendorFilters(vendors),
                this.tableReadyTransactions,
                this.panelTaskFilters,
                this.onFilter.bind(this)
            );
            if (vendors.length == 1) {
                this.filterPanel.toggleFilterById(vendors[0].id);
            }
        }
    }

    /** This is an event handler for the onRowDisplay event of tableReadyTransactions. */
    tableReadyTransactionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const labelChainCode = tableRow.findComponentById("labelChainCode");
        CommonTooltips.setTooltipContentLayout(labelChainCode, null, "lme/integrations/fuel/FuelChainCodeQuickInfo", null,
            { interface_id: tableRow.data.get("interface_id"), code: tableRow.data.get("chain_code") }
        );
    }

    onFilter(filter: SecondaryFilter) {
        this.processingHandler.multiButton.enabled = this.filterPanel.getActiveFilter() != null && this.tableReadyTransactions.displayedRowCount > 0;
    }

    processingActionOnPost(action: Action) {
        this.processingHandler.showSlideout({
            title: `Process Fuel Transaction (${this.tableReadyTransactions.displayedRowCount}) Records`,
            doAfterSlideIn: (decorator) => {
                const stepper = decorator.layout as ProcessingStepper;
                stepper.calculatePostableTotals = this.calculatePostableTotals.bind(this);
                stepper.getApiPayload = (posting: boolean) => ({ fuel_interface_id: this.filterPanel.getActiveFilter().id, is_posting: posting });
                stepper.doAfterPost = () => {
                    this.filterPanel.clearActiveFilter();
                    this.mainDataSource.search();
                }
                switch (action) {
                    case Action.POST:
                        stepper.continue();
                        break;
                    case Action.PRINT_AND_POST:
                        stepper.displayReport();
                        break;
                }
            }
        });
    }

    async calculatePostableTotals() {
        const sum = new Currency(0);
        for (const row of this.tableReadyTransactions.filteredRows) {
            const fundedAmount = row.data.getCurrency("funded_amount");
            if (fundedAmount) {
                await sum.add(fundedAmount);
            }
            const directAmount = row.data.getCurrency("direct_amount");
            if (directAmount) {
                await sum.add(directAmount);
            }
        }
        return { count: this.tableReadyTransactions.filteredRows.length, amount: sum };
    }

}
